<template>
    <div class="store-contact-order-form-calendar">
        <div class="row">
            <div v-if="loading" class="col-md-12 text-center">
                <div class="mb-5 font-weight-bold">
                    <i class="fas fa-spinner fa-spin"></i> Chargement du calendrier...
                </div>
            </div>
            <div v-else-if="errors && errors.length" class="col-md-12">
                <ErrorsZone :errors="errors"></ErrorsZone>
            </div>
            <template v-else-if="loaded">
                <Calendar
                    :min-date="minDate"
                    :max-date="maxDate"
                    :disabled-dates="disabledDates"
                    :available-dates="availableDates"
                    :attributes="attributes"
                    :masks="{
                        title: 'MMMM YYYY',
                        weekdays: 'WWWW',
                    }"
                    locale="fr"
                    is-expanded
                    is-required
                    trim-weeks
                >
                    <template #day-content="{ day, attributes, dayEvents }">
                        <div class="day-cell" :class="[attributes && attributes.length ? 'day-' + attributes[0].customData.higherWarnLevel : '']">
                            <span class="day-label">{{ day.day }}</span>
                            <div class="day-content">
                                <template v-if="attributes && attributes.length">
                                    <template v-if="attributes[0].customData.higherWarnLevel === 'allowed'">
                                        <button class="btn btn-success" @click="onAllowedDayClick(day)">
                                            <i class="fas fa-cart-plus"></i><span class="btn-text"> Commander</span>
                                        </button>
                                    </template>
                                    <template v-if="attributes[0].customData.order">
                                        <Modal>
                                            <template #button="{ showModal }">
                                                <button type="button" class="btn btn-warning" @click="showModal">
                                                    <i class="fas fa-edit"></i><span class="btn-text"> Modifier la commande</span>
                                                </button>
                                            </template>
                                            <template #body>
                                                <div class="text-left">
                                                    La commande <a :href="'/espace-magasins/magasin/' + attributes[0].customData.order.fk_customer_id + '/commandes/' + attributes[0].customData.order.id">{{ attributes[0].customData.order.reference }}</a> existe déjà pour ce jour de livraison.
                                                    <br>
                                                    Voulez-vous la modifier ?
                                                </div>
                                            </template>
                                            <template #footer="{ closeModal }">
                                                <button type="button" class="btn btn-secondary" @click="closeModal">
                                                    <i class="fas fa-chevron-left"></i> Revoir le calendrier
                                                </button>
                                                <a class="btn btn-warning" :href="'/espace-magasins/magasin/' + attributes[0].customData.order.fk_customer_id + '/commandes/' + attributes[0].customData.order.id + '/modifier'">
                                                    <i class="fas fa-edit"></i> Modifier la commande
                                                </a>
                                            </template>
                                        </Modal>
                                    </template>
                                    <template v-else-if="attributes[0].customData.has_recurrent_order">
                                        <button class="btn btn-outline-secondary bg-white" @click="onRecurrentOrderDayClick(day)">
                                            <i class="fas fa-shopping-cart"></i>
                                        </button>
                                    </template>
                                    <template v-else-if="attributes[0].customData.higherWarnLevel === 'restricted'">
                                        <button class="btn btn-outline-secondary bg-white" @click="onRestrictedDayClick(day)">
                                            <i class="fas fa-info-circle"></i>
                                        </button>
                                    </template>
                                </template>
                            </div>
                        </div>
                    </template>
                </Calendar>
                <Modal ref="recurrentOrderDayModal">
                    <template #body>
                        Une commande hebdomadaire automatique existe déjà pour ce jour de livraison.
                    </template>
                    <template #footer="{ closeModal }">
                        <button type="button" class="btn btn-secondary" @click="closeModal">
                            <i class="fas fa-chevron-left"></i> Revoir le calendrier
                        </button>
                    </template>
                </Modal>
                <Modal ref="restrictedDayModal">
                    <template #body>
                        Vous ne pouvez pas commander en ligne pour ce jour de livraison.<br>
                        Contactez nous pour passer commande.
                    </template>
                    <template #footer="{ closeModal }">
                        <button type="button" class="btn btn-secondary" @click="closeModal">
                            <i class="fas fa-chevron-left"></i> Revoir le calendrier
                        </button>
                    </template>
                </Modal>
            </template>
            <div v-else class="col-md-12">
                <div class="alert alert-danger">
                    Erreur lors du chargement des dates de livraison. Essayez de recharger la page.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {useGetLoader} from '../compositions/axios/loadingRequest';
    import {momentDay} from "../../moment-with-helpers";

    import ErrorsZone from "../ErrorsZone";
    import {Calendar} from "v-calendar";
    import Modal from "../Modal";

    export default {
        components: {
            Calendar,
            ErrorsZone,
            Modal,
        },

        props: {
            order: {
                type: Object,
                required: false,
            },
            store: {
                type: Object,
                required: true,
            },
            value: {
                type: Object,
                required: false,
            },
            isRecurrent: {
                type: Boolean,
                required: false,
                default: false,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            return {
                loaded,
                loading,
                errors,
                load,
            };
        },

        data: function () {
            return {
                selectedDate: this.value && momentDay(this.value.date).toDate(),

                calendar: null,
            };
        },

        created() {
            this.loadDates();
        },

        methods: {
            onAllowedDayClick(day) {
                this.selectedDate = day.date;
                this.$emit('dayclick', this.buildEventDateChangePayload(day.date));
            },

            onRecurrentOrderDayClick(day) {
                this.$refs.recurrentOrderDayModal.showModal();
            },

            onRestrictedDayClick(day) {
                this.$refs.restrictedDayModal.showModal();
            },

            getDatePermission(date) {
                if (this.calendar && this.calendar.authorizations) {
                    let authorization = this.calendar.authorizations[momentDay(date).format('YYYY-MM-DD')];
                    if (!authorization) {
                        authorization = {
                            authorizations: [
                                {
                                    permission: "too_far_away_in_past_or_future",
                                    reason: "Jour trop loin dans le passé ou dans le futur",
                                    warnLevel: "restricted",
                                }
                            ],
                            higherWarnLevel: 'forbidden',
                        }
                    }
                    authorization['date'] = momentDay(date).toDate();
                    return authorization || null;
                }
            },

            async loadDates() {
                let url = `/espace-magasins/magasin/${this.store.id}/calendrier-dates-livraison` + (this.isRecurrent ? '-commandes-recurrentes' : '');
                if(this.order) {
                    url += `/${this.order.id}`
                }
                const data = await this.load(url);
                if (data) {
                    this.calendar = data;
                }
            },

            buildEventDateChangePayload(date) {
                if (date) {
                    return this.getDatePermission(date);
                }
                return null;
            },
        },

        computed: {
            disabledDates() {
                return { start: null, end: null };
            },

            availableDates() {
                if (this.calendar && this.calendar.authorizations) {
                    return Object.keys(this.calendar.authorizations).reduce((acc, date) => {
                        if (this.calendar.authorizations[date].higherWarnLevel !== 'forbidden') {
                            acc.push(date);
                        }
                        return acc;
                    }, []);
                }
                return [];
            },

            minDate() {
                if (this.calendar && this.calendar.authorizations) {
                    const keys = Object.keys(this.calendar.authorizations).sort();
                    if (keys.length > 0) {
                        return keys[0];
                    }
                }
                return null;
            },

            maxDate() {
                if (this.calendar && this.calendar.authorizations) {
                    const keys = Object.keys(this.calendar.authorizations).sort();
                    if (keys.length > 0) {
                        return keys[keys.length - 1];
                    }
                }
                return null;
            },

            attributes() {
                let attrs = [];
                if (this.calendar && this.calendar.authorizations) {
                    for (const day in this.calendar.authorizations) {
                        const dayAuthorizations = this.calendar.authorizations[day];

                        let customData = dayAuthorizations;
                        for (const i in dayAuthorizations.authorizations) {
                            const authorization = dayAuthorizations.authorizations[i];
                            if (authorization.permission) {
                                 if (authorization.permission.indexOf('another_order_on_same_') === 0 && authorization.metadata && authorization.metadata.order) {
                                    customData['order'] = authorization.metadata.order;
                                }
                                if (authorization.permission.indexOf('another_recurrent_order_on_same_delivery_day') === 0) {
                                    customData['has_recurrent_order'] = true;
                                }
                            }
                        }

                        if (dayAuthorizations.higherWarnLevel !== 'forbidden') {
                            attrs.push({
                                dates: day,
                                customData: customData,
                            });
                        } else {
                            attrs.push({
                                dates: day,
                                excludeDates: day
                            });
                        }
                    }
                }
                return attrs;
            },
        }
    }
</script>

<style lang="scss">
.store-contact-order-form-calendar {
    .btn-text {
        font-size: 13px;
    }

    @media (max-width: 1400px){
        .btn-text {
            display: none;
        }
    }

    .vc-container {
        --day-border: 1px solid #b8c2cc;
        --day-border-highlight: 1px solid #b8c2cc;
        --day-width: 110px;
        --day-height: 80px;
        --weekday-bg: #f8fafc;
        --weekday-border: 1px solid #eaeaea;
        border-radius: 0;

        @media (max-width: 1400px){
            --day-width: 80px;
            --day-height: 80px;
        }

        & .vc-title {
            text-transform: capitalize;
        }

        & .vc-header {
            background-color: #f1f5f8;
            padding: 10px 0;
        }

        & .vc-weeks {
            padding: 0;

            @media (max-width: 1400px){
                overflow-x: auto;
            }
        }

        & .vc-weekday {
            background-color: var(--weekday-bg);
            border-bottom: var(--weekday-border);
            border-top: var(--weekday-border);
            padding: 5px 0;
            text-transform: capitalize;
            color: var(--gray-800);
        }

        & .vc-day {
            padding: 0 5px 3px 5px;
            text-align: left;
            height: var(--day-height);
            min-width: var(--day-width);
            background-color: white;
            z-index: unset;

            &.weekday-1,
            &.weekday-7 {
                background-color: #eff8ff;
            }

            &:not(.on-bottom) {
                border-bottom: var(--day-border);

                &.weekday-1 {
                    border-bottom: var(--day-border-highlight);
                }
            }

            &:not(.on-right) {
                border-right: var(--day-border);
            }

            .day-cell {
                height: var(--day-height);
            }

            .day-label {
                position: absolute;
                top: 3px;
                left: 5px;
            }

            .day-content {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100%;
            }

            &.is-today  {
                background: rgba(0, 0, 0, 0.07);
            }
        }

        & .vc-day-dots {
            margin-bottom: 5px;
        }
    }
}
</style>
